import React, { useState } from 'react'

import type { HouseTreeNode } from '../types/catalog'

interface HouseTreeNodeViewProps {
  node: HouseTreeNode
  selected: string[]
  onToggleLeaf: (leaf: string) => void
  filter: string
  depth: number
}

function leafValuesIn(nodes: HouseTreeNode[]): string[] {
  const out: string[] = []
  const walk = (segments: HouseTreeNode[]) => {
    for (const node of segments) {
      if (node.leafValue) out.push(node.leafValue)
      if (node.children) walk(node.children)
    }
  }
  walk(nodes)
  return out
}

function nodeMatchesFilter(node: HouseTreeNode, query: string): boolean {
  if (!query) return true
  if (node.label.toLowerCase().includes(query)) return true
  if (node.children && node.children.some((child) => nodeMatchesFilter(child, query))) return true
  return false
}

const HouseTreeNodeView: React.FunctionComponent<HouseTreeNodeViewProps> = (props) => {
  const { node, selected, onToggleLeaf, filter, depth } = props
  const [open, setOpen] = useState(true)
  const trimmedQuery = filter.trim().toLowerCase()
  if (!nodeMatchesFilter(node, trimmedQuery)) return null

  if (node.leafValue) {
    return (
      <label className="device-dropdown-item" style={{ paddingLeft: 12 + depth * 14 }}>
        <input
          type="checkbox"
          checked={selected.includes(node.leafValue)}
          onChange={() => onToggleLeaf(node.leafValue as string)}
        />{' '}
        {node.label}
      </label>
    )
  }

  const branchLeaves = leafValuesIn(node.children || [])
  const allChecked =
    branchLeaves.length > 0 && branchLeaves.every((leaf) => selected.includes(leaf))
  const someChecked = branchLeaves.some((leaf) => selected.includes(leaf))

  return (
    <div className="tree-node">
      <div
        className="tree-node-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          paddingLeft: 8 + depth * 14,
          cursor: 'pointer',
          fontSize: 12,
          color: 'var(--black)',
        }}
        onClick={(event) => {
          if ((event.target as HTMLElement).tagName === 'INPUT') return
          setOpen((value) => !value)
        }}
      >
        <span
          className="tree-chevron"
          style={{
            display: 'inline-block',
            width: 10,
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 120ms',
            color: '#94a3b8',
            fontSize: 10,
          }}
        >
          ▶
        </span>
        <input
          type="checkbox"
          checked={allChecked}
          ref={(el) => {
            if (el) el.indeterminate = !allChecked && someChecked
          }}
          onChange={() => {
            if (allChecked) {
              for (const leaf of branchLeaves) {
                if (selected.includes(leaf)) onToggleLeaf(leaf)
              }
            } else {
              for (const leaf of branchLeaves) {
                if (!selected.includes(leaf)) onToggleLeaf(leaf)
              }
            }
          }}
          onClick={(event) => event.stopPropagation()}
        />
        <span>
          {node.label}
          {typeof node.count === 'number' ? ` (${node.count})` : ''}
        </span>
      </div>
      {open && node.children && (
        <div className="tree-children open">
          {node.children.map((child, index) => (
            <HouseTreeNodeView
              key={`${child.label}-${index}`}
              node={child}
              selected={selected}
              onToggleLeaf={onToggleLeaf}
              filter={filter}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface HouseTreeBrowserProps {
  tree: HouseTreeNode[]
  selected: string[]
  onToggleLeaf: (leaf: string) => void
  filter: string
}

export const HouseTreeBrowser: React.FunctionComponent<HouseTreeBrowserProps> = (props) => {
  const { tree, selected, onToggleLeaf, filter } = props
  return (
    <>
      {tree.map((node, index) => (
        <HouseTreeNodeView
          key={`${node.label}-${index}`}
          node={node}
          selected={selected}
          onToggleLeaf={onToggleLeaf}
          filter={filter}
          depth={0}
        />
      ))}
    </>
  )
}
